import platform from '@play-co/gcinstant';
import { analytics } from '@play-co/gcinstant';
import { createQueryablePromiseFromPromise } from '@play-co/timestep-core/ui';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import statePromise from 'src/lib/statePromise';
import { isSceneEntered } from 'src/lib/stateUtils';
import {
  getCreativeSuffix,
  getSceneManager,
  waitForAssets,
  waitForItPromise,
  withLoading,
} from 'src/lib/utils';
import { duoMugAssets, petSpeciesAssets } from 'src/loadingGroups';
import { Care } from 'src/replicant/buruburu/care';
import { Pet } from 'src/replicant/buruburu/pet';
import { Duo } from 'src/replicant/duo';
import { DuoFriend } from 'src/replicant/duo/duo.asyncGetters';
import { duration } from 'src/replicant/utils/duration';
import { setPetCrateOpening } from 'src/state/ui';
import StateObserver from 'src/StateObserver';
import { CareItemController } from '../care/CareItemController';
import { startConversation } from '../conversation/conversation.redux';
import { levelUpSequence } from '../level/level.sequence';
import { EntityManager, CareItemEntity } from '../logic/EntityManager';
import { getFriendsPet, getPet } from '../pet';
import { getTutorialController } from '../tutorial/TutorialController';
import { endVisitSession, startVisitSession } from '../visit/visit.sequence';
import { duoActions } from './duo.redux';

/**
 * Invite visited pet to start a duo.
 * @returns
 */
export async function startDuoInviteSequence() {
  // Await for tutorial to complete.
  await getTutorialController().handleEvent('duoInviteSequenceStarted');

  let careItemId = null;
  do {
    careItemId = await openPopupPromise('popupSelectCareItem', {
      type: 'invitation',
      stage: Pet.getters(StateObserver.getState().user).getCurrentEvolution()
        .stage,
    });
    if (
      careItemId &&
      (await openPopupPromise('popupConfirmCareItem', { careItemId }))
    ) {
      break;
    }
  } while (careItemId);

  // No care item selected. Bail.
  if (!careItemId) return;

  const { consumed } = await StateObserver.invoke.purchaseCareItem(careItemId);

  if (!consumed) return;

  const pet = getPet();
  const friendsPet = getFriendsPet();

  const { user } = StateObserver.getState();

  analytics.pushEvent('DuoInviteSend', {
    friend: Object.keys(user.friends).includes(friendsPet.id),
    friendPetID: friendsPet.id,
    item: careItemId,
    count: user.duo.invitations.sent.filter(
      ({ playerId }) => playerId === friendsPet.id,
    ).length,
    bPointsSpent: Care.ruleset.careItems[careItemId]?.price,
  });

  const invitationPromise = createQueryablePromiseFromPromise(
    StateObserver.invoke.sendDuoInvitation({
      playerId: friendsPet.id,
      item: careItemId,
    }),
  );

  pet.position.setWandering(false);
  friendsPet.position.setWandering(false);

  await Promise.all([
    pet.moveTo({
      scene: 'friendsLivingRoom',
      location: 'lm',
    }),
    friendsPet.moveTo({
      scene: 'friendsLivingRoom',
      location: 'rm',
    }),
  ]);

  pet.appearance.setAction(
    careItemId === 'i_ultimateVowsCoffee' ? 'inviteUltimate' : 'inviteNormal',
  );
  friendsPet.appearance.setAction('invited');

  await startConversation(pet.id, 'duo_inviter_invite', pet.species, 'normal');
  await startConversation(
    friendsPet.id,
    'duo_invitee_invited',
    friendsPet.species,
    'normal',
  );

  if (!invitationPromise.isReady()) {
    await withLoading(() => invitationPromise);
  }

  await endVisitSession(friendsPet.id, true, false);

  await startConversation(pet.id, 'duo_inviter_home', pet.species, 'normal');
}

/**
 * Handle incoming duo invitation.
 * @returns
 */
export async function startDuoInvitationReceivedSequence() {
  const { user } = StateObserver.getState();
  const now = StateObserver.now();
  const invitations = Duo.api.getNonExpiredReceivedInvitations(user, now);
  const latestInvitation = invitations[invitations.length - 1];

  // User already saw invitation. Bail.
  if (!latestInvitation || latestInvitation.timestamp < user.duo.lastViewed) {
    return;
  }
  const pet = getPet();

  await startConversation(pet.id, 'duo_invitee_home', pet.species, 'normal');

  await getTutorialController().handleEvent('duoInvitationReceived');

  await openDuoPopup();
}

/**
 * Open duo invitations popup.
 * @param tab - Tab to open. Defaults to 'received'.
 * @returns
 */
export async function openDuoPopup(tab: 'received' | 'sent' = 'received') {
  await StateObserver.invoke.updateDuoLastViewed();

  const { sent, received } = await withLoading(() =>
    StateObserver.replicant.asyncGetters.getDuoInvitationsDetails(),
  );

  // Clear after fetching to make sure user sees the invitation.
  await StateObserver.invoke.clearExpiredDuoInvitations();

  return openPopupPromise('popupDuoInvitations', {
    sent,
    received,
    selectedTab: tab,
  });
}

export async function startRespondToDuoSequence(friend: DuoFriend) {
  // Block emitter from triggering duo sequence.
  StateObserver.dispatch(duoActions.setCanDebut(false));

  const response = await openPopupPromise('popupDuoResponse', friend);

  if (!response) return;

  await withLoading(() =>
    StateObserver.invoke.sendDuoResponse({
      playerId: friend.id,
      timestamp: friend.invitation.timestamp,
      accepted: response.accepted,
      message: response.message,
    }),
  );

  await openPopupPromise('popupDuoResponseSuccess', {
    name: friend.profile.name,
    photo: friend.profile.photo,
    accepted: response.accepted,
  });

  await startDuoResponseSequence();

  StateObserver.dispatch(duoActions.setCanDebut(true));
}

/**
 * Handle last duo response. Will trigger debut if accepted.
 * @returns
 */

let responseSequenceRunning = false;
export async function startDuoResponseSequence() {
  // Already running. Bail.
  if (responseSequenceRunning) return false;

  // Set running flag.
  responseSequenceRunning = true;

  try {
    const { user } = StateObserver.getState();
    const {
      lastResponse,
      debut,
      invitations: { sent },
    } = user.duo;

    // User already saw invitation. Bail.
    if (!lastResponse && !debut) {
      return false;
    }

    const { getCurrentEvolution, getPetState } = Pet.getters(user);

    const { species, stage } = getCurrentEvolution();

    // Something is wrong, cannot process response if pet is not an adult.
    // This may happen if a new pet is spawned while another player is trying to debut with this pet.
    if (stage !== 'adult') {
      // Should just delete lastResponse and bail.
      await StateObserver.invoke.deleteLastDuoResponse();
      return false;
    }

    // Figure out if current player is the inviter or invitee.
    const isPlayerInviter = sent.some(
      (x) =>
        x.playerId === lastResponse.playerId &&
        x.timestamp === lastResponse.timestamp,
    );

    if (isPlayerInviter) {
      await startConversation(
        platform.playerID,
        'duo_inviter_notified',
        species,
        getPetState(StateObserver.now(), false),
      );
    }

    const [details] = await withLoading(() =>
      Promise.all([
        StateObserver.replicant.asyncGetters.getDuoInvitationsDetails(),
        waitForAssets(duoMugAssets),
      ]),
    );

    const invitations = isPlayerInviter ? details.sent : details.received;
    const friend = invitations.find(
      ({ invitation: { timestamp }, id }) =>
        timestamp === lastResponse.timestamp && id === lastResponse.playerId,
    );

    const currentScene = isPlayerInviter ? 'friendsLivingRoom' : 'livingRoom';

    getPet().position.setWandering(false);

    await startVisitSession({
      friendId: friend.id,
      // Flip positions when player is not inviter.
      playerTargetPosition: {
        scene: currentScene,
        location: isPlayerInviter ? 'lm' : 'rm',
      },
      friendTargetPosition: {
        scene: currentScene,
        location: isPlayerInviter ? 'rm' : 'lm',
      },
      // Use invitation's pet data.
      overrides: {
        evolution: {
          species: friend.pet.species,
          stage: 'adult',
        },
        petName: friend.pet.name,
        age: friend.pet.age,
      },
    });

    const inviter = isPlayerInviter ? getPet() : getFriendsPet();
    inviter.position.setWandering(false);
    const invitee = isPlayerInviter ? getFriendsPet() : getPet();
    invitee.position.setWandering(false);

    const type =
      friend.invitation.item === 'i_ultimateVowsCoffee' ? 'Ultimate' : 'Normal';

    inviter.appearance.setAction(`invite${type}`);
    invitee.appearance.setAction('invited');

    await startConversation(
      inviter.id,
      'duo_inviter_invite',
      inviter.species,
      'normal',
    );

    await startConversation(
      invitee.id,
      'duo_invitee_answer',
      invitee.species,
      'normal',
    );

    if (!lastResponse.accepted) {
      await startConversation(
        invitee.id,
        'duo_invitee_refuse',
        invitee.species,
        'normal',
      );

      inviter.appearance.setAction(`refused${type}`);

      if (friend.invitation.message) {
        await startConversation(
          invitee.id,
          'duo_invitee_message_intro',
          invitee.species,
          'normal',
        );

        await startConversation(
          invitee.id,
          'duo_invitee_message',
          friend.pet.species,
          'normal',
        );
      }

      await startConversation(
        inviter.id,
        'duo_inviter_refused',
        inviter.species,
        'normal',
      );

      inviter.position.setWandering(false);
      invitee.position.setWandering(false);

      await endVisitSession(friend.id, true, false);

      await StateObserver.invoke.deleteLastDuoResponse();

      return false;
    }

    await startConversation(
      invitee.id,
      'duo_invitee_accept',
      invitee.species,
      'happy',
    );

    inviter.position.setWandering(false);
    invitee.position.setWandering(false);

    const careItemEntityId = EntityManager.getEntityId(
      'careItem',
      friend.invitation.item,
      friend.id,
    );
    const careItem = new CareItemController(
      getSceneManager(),
      EntityManager.createOrGetEntity(careItemEntityId) as CareItemEntity,
    );

    await careItem.position.moveTo({
      scene: currentScene,
      location: 'mugCenter',
    });

    careItem.appearance.animate();

    inviter.appearance.setAction(`accepted`);

    invitee.appearance.setAction(`accepted`);
    invitee.appearance.setFlipped(true);

    await waitForItPromise(duration({ seconds: 2 }));

    // Don't try to claim reward if already claimed, can happen if sequence was interrupted.
    if (!debut.rewardClaimed) {
      await openPopupPromise('popupDuoReward', {
        item: friend.invitation.item,
      });

      await StateObserver.invoke.claimDuoDebutReward();
    }

    await startDebutSequence(friend, careItem);

    await StateObserver.invoke.deleteLastDuoResponse();

    return true;
  } finally {
    // Reset flag.
    // eslint-disable-next-line require-atomic-updates
    responseSequenceRunning = false;
  }
}

/**
 * Trigger debut sequence.
 * @param friend - Friend to debut with.
 * @param careItem - Care item to destroy after transition.
 */
export async function startDebutSequence(
  friend: DuoFriend,
  careItem?: CareItemController,
) {
  const friendPetController = getFriendsPet();

  friendPetController.init({
    pet: {
      species: friend.pet.species,
    },
    position: {
      scene: 'debut',
      location: 'right',
    },
  });

  friendPetController.appearance.setFlipped(true);
  friendPetController.appearance.setAction('rocketIdle');

  const pet = getPet();

  await pet.moveTo({
    scene: 'debut',
    location: 'left',
  });

  await statePromise(() => isSceneEntered('debut'));

  // It's safe to dispose of the mug now. Since it's not visible anymore.
  careItem?.destroy();

  pet.appearance.setAction('rocketIdle');

  const scene = getSceneManager().scenes.debut;

  await scene.launch();

  pet.appearance.setAction('rocketLaunch');
  friendPetController.appearance.setAction('rocketLaunch');

  await waitForItPromise(duration({ seconds: 2 }));

  pet.hide();
  friendPetController.hide();

  await waitForItPromise(duration({ seconds: 1 }));

  await Promise.all([
    pet.moveTo({
      scene: 'debut',
      location: 'shardLeft',
    }),
    friendPetController.moveTo({
      scene: 'debut',
      location: 'shardRight',
    }),
  ]);

  pet.appearance.setAction('shardIdle');
  friendPetController.appearance.setFlipped(true);
  friendPetController.appearance.setAction('shardIdle');
  pet.show();
  friendPetController.show();

  await waitForItPromise(duration({ seconds: 2 }));

  await scene.spawnBox();

  pet.appearance.setAction('shardBox');
  friendPetController.appearance.setAction('shardBox');

  void waitForItPromise(duration({ seconds: 0.8 })).then(() => {
    pet.hide();
    friendPetController.hide();
  });

  await scene.shakeBox();

  await startOpenBoxSequence();
}

/**
 * Open duo debut box.
 */
async function startOpenBoxSequence() {
  let pet = getPet();

  // Freeze pet
  pet.appearance.setAction(null);
  pet.appearance.setSpecies(null);
  pet.position.setWandering(false);

  // Hide pet information
  StateObserver.dispatch(setPetCrateOpening(true));

  // Show dialog
  await waitForItPromise(500);
  await openPopupPromise('popupAcceptToS', { showTos: false });

  // Create new pet
  await StateObserver.invoke.debut();

  // Load assets for species
  const { user } = StateObserver.getState();
  const { species } = Pet.getters(user).getCurrentEvolution();
  await withLoading(() => waitForAssets(petSpeciesAssets[species]));

  // Set species skin
  pet.appearance.setSpecies(species);

  // Show pet movie clip
  const scene = getSceneManager().scenes.debut;
  await scene.openBox();
  pet.show();

  // Set pet positions
  await pet.moveTo({
    scene: 'debut',
    location: 'center',
  });

  // Show greetings messages
  const evolution = Pet.ruleset.evolutions[species];
  const message = evolution[`greetings_${getCreativeSuffix()}`];
  await openPopupPromise('popupSpeechBubble', { message });

  // Ask new name
  const currentName = Pet.getters(user).getPetName();
  const name = await openPopupPromise('popupPetNameInput', {
    name: currentName,
  });

  // Update actual name
  await StateObserver.invoke.updateName(name);

  // Show level up popup if needed.
  await levelUpSequence();

  // Update name
  pet.appearance.setAttributes({ name });

  // Show new pet information
  StateObserver.dispatch(setPetCrateOpening(false));

  // Schedule activity
  await StateObserver.invoke.schedulePetActivity();

  await scene.hideGodrays();

  // Go back home.
  await endVisitSession(getFriendsPet().id, true, false);
}

/**
 * Send reminder to an invited friend.
 * @param friend - Friend to send reminder to.
 * @returns
 */
export async function sendDuoReminder(friend: DuoFriend) {
  const response = await openPopupPromise(
    'popupDuoReminderConfirmation',
    friend,
  );

  if (!response) return;

  await withLoading(() =>
    StateObserver.invoke.sendDuoReminder({
      playerId: friend.id,
      timestamp: friend.invitation.timestamp,
    }),
  );

  const { user } = StateObserver.getState();

  analytics.pushEvent('DuoInviteReminderSend', {
    friend: Object.keys(user.friends).includes(friend.id),
    friendPetID: friend.pet.species,
    count: user.duo.invitations.sent.filter(
      ({ playerId }) => playerId === friend.id,
    ).length,
    bPointsSpent: Care.ruleset.careItems[friend.invitation.item]?.price,
  });

  await openPopupPromise('popupDuoReminderSuccess', {
    name: friend.profile.name,
    photo: friend.profile.photo,
  });
}
